import type {
  ActionItemAssignmentStatus,
  ActionItemCloseReason,
  ActionItemEventKind,
  ActionItemLifecycle,
  ActionItemPriority,
  ActionItemReasonLabel,
} from '@/features/action-items/types';
import type { ProjectScope } from '@/features/sync/project-scope';
import type { Unsubscribe } from './subscription.ts';

export interface ActionItemsManagementSnapshot {
  status: ActionItemsManagementStatus;
  items: PublicActionItem[];
  events: PublicActionItemEvent[];
  reviewedEvidenceKeys: string[];
}

export type ActionItemsManagementStatus = 'loading' | 'ready' | 'reconnecting' | 'error';

export type PublicActionItemProvenance = { kind: 'manual' } | { kind: 'draftReview' };

export interface PublicActionItem {
  id: string;
  title: string;
  lifecycle: ActionItemLifecycle;
  reasonLabels: ActionItemReasonLabel[];
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;
  provenance: PublicActionItemProvenance;
  description?: string;
  priority?: ActionItemPriority;
  dueDate?: string;
  department?: string;
  closeReason?: ActionItemCloseReason;
  mergedIntoId?: string;
  sanitizedProvenanceSummary?: string;
}

export interface PublicActionItemEvent {
  id: string;
  actionItemId: string;
  kind: ActionItemEventKind;
  occurredAt: string;
  actorId?: string;
  assignmentId?: string;
  personnelId?: string;
  fromStatus?: ActionItemAssignmentStatus;
  toStatus?: ActionItemAssignmentStatus;
  summary: string;
}

export interface ActionItemsManagementPayload {
  items: PublicActionItem[];
  events: PublicActionItemEvent[];
  reviewedEvidenceKeys: string[];
}

type UnknownRecord = Record<string, unknown>;

const PUBLIC_ITEM_FIELDS = new Set([
  'id', 'title', 'lifecycle', 'reasonLabels', 'createdAt', 'createdBy', 'updatedAt',
  'updatedBy', 'provenance', 'description', 'priority', 'dueDate', 'department',
  'closeReason', 'mergedIntoId', 'sanitizedProvenanceSummary',
]);
const PUBLIC_EVENT_FIELDS = new Set([
  'id', 'actionItemId', 'kind', 'occurredAt', 'actorId', 'assignmentId', 'personnelId',
  'fromStatus', 'toStatus', 'summary',
]);
const REASON_LABELS = new Set<ActionItemReasonLabel>([
  'explicit_action', 'unresolved_concern', 'neglected_topic', 'project_check',
]);
const PRIORITIES = new Set<ActionItemPriority>(['low', 'normal', 'high', 'urgent']);
const CLOSE_REASONS = new Set<ActionItemCloseReason>(['resolved', 'dismissed', 'merged', 'archived']);
const EVENT_KINDS = new Set<ActionItemEventKind>([
  'item_created', 'item_updated', 'item_closed', 'item_reopened', 'item_merged',
  'draft_promoted', 'assignment_created', 'assignment_reactivated',
  'assignment_status_changed', 'assignment_removed',
]);
const ASSIGNMENT_STATUSES = new Set<ActionItemAssignmentStatus>([
  'assigned', 'in_progress', 'done', 'blocked', 'declined', 'removed',
]);
const CANONICAL_EVIDENCE_KEY_CHARACTERS = /^[A-Za-z0-9._~!$&'()*+,;=:@%-]+$/;

function strictRecord(value: unknown, allowedFields: ReadonlySet<string>, label: string): UnknownRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  const record = value as UnknownRecord;
  for (const field of Object.keys(record)) {
    if (!allowedFields.has(field)) throw new Error(`${label} has unknown field "${field}"`);
  }
  return record;
}

function requiredString(value: UnknownRecord, field: string, label: string): string {
  const candidate = value[field];
  if (typeof candidate !== 'string' || !candidate.trim()) {
    throw new Error(`${label} ${field} is required`);
  }
  return candidate;
}

function optionalString(value: UnknownRecord, field: string, label: string): string | undefined {
  if (!Object.hasOwn(value, field)) return;
  const candidate = value[field];
  if (typeof candidate !== 'string' || !candidate.trim()) {
    throw new Error(`${label} ${field} is invalid`);
  }
  return candidate;
}

function decodePublicProvenance(value: unknown): PublicActionItemProvenance {
  const provenance = strictRecord(value, new Set(['kind']), 'Action Item public provenance');
  if (provenance.kind !== 'manual' && provenance.kind !== 'draftReview') {
    throw new Error('Action Item public provenance kind is invalid');
  }
  return { kind: provenance.kind };
}

function decodeReasonLabels(value: unknown): ActionItemReasonLabel[] {
  if (!Array.isArray(value)) {
    throw new Error('Action Item public reasonLabels is invalid');
  }
  const candidates = value as unknown[];
  if (!candidates.every(label => REASON_LABELS.has(label as ActionItemReasonLabel))) {
    throw new Error('Action Item public reasonLabels is invalid');
  }
  return candidates.map(label => label as ActionItemReasonLabel);
}

function decodePublicItemLifecycle(
  item: UnknownRecord,
): Pick<PublicActionItem, 'lifecycle' | 'closeReason'> {
  if (item.lifecycle !== 'active' && item.lifecycle !== 'closed') {
    throw new Error('Action Item public lifecycle is invalid');
  }
  const closeReason = optionalString(item, 'closeReason', 'Action Item public record');
  if (item.lifecycle === 'closed') {
    if (!closeReason || !CLOSE_REASONS.has(closeReason as ActionItemCloseReason)) {
      throw new Error('Closed Action Item public closeReason is invalid');
    }
    return { lifecycle: item.lifecycle, closeReason: closeReason as ActionItemCloseReason };
  }
  if (closeReason !== undefined) {
    throw new Error('Active Action Item public record cannot include closeReason');
  }
  return { lifecycle: item.lifecycle };
}

function decodePublicItemOptionalFields(item: UnknownRecord): Partial<PublicActionItem> {
  const projected: Partial<PublicActionItem> = {};
  const stringFields = [
    'createdBy', 'updatedBy', 'description', 'dueDate', 'department',
    'mergedIntoId', 'sanitizedProvenanceSummary',
  ] as const;
  for (const field of stringFields) {
    const candidate = optionalString(item, field, 'Action Item public record');
    if (candidate !== undefined) Object.assign(projected, { [field]: candidate });
  }
  const priority = optionalString(item, 'priority', 'Action Item public record');
  if (priority && !PRIORITIES.has(priority as ActionItemPriority)) {
    throw new Error('Action Item public priority is invalid');
  }
  if (priority) projected.priority = priority as ActionItemPriority;
  return projected;
}

function decodePublicActionItem(value: unknown): PublicActionItem {
  const item = strictRecord(value, PUBLIC_ITEM_FIELDS, 'Action Item public record');
  return {
    id: requiredString(item, 'id', 'Action Item public record'),
    title: requiredString(item, 'title', 'Action Item public record'),
    ...decodePublicItemLifecycle(item),
    reasonLabels: decodeReasonLabels(item.reasonLabels),
    createdAt: requiredString(item, 'createdAt', 'Action Item public record'),
    updatedAt: requiredString(item, 'updatedAt', 'Action Item public record'),
    provenance: decodePublicProvenance(item.provenance),
    ...decodePublicItemOptionalFields(item),
  };
}

function optionalAssignmentStatus(
  event: UnknownRecord,
  field: string,
): ActionItemAssignmentStatus | undefined {
  const candidate = optionalString(event, field, 'Action Item public event');
  if (candidate && !ASSIGNMENT_STATUSES.has(candidate as ActionItemAssignmentStatus)) {
    throw new Error(`Action Item public event ${field} is invalid`);
  }
  return candidate as ActionItemAssignmentStatus | undefined;
}

function decodePublicActionItemEvent(value: unknown): PublicActionItemEvent {
  const event = strictRecord(value, PUBLIC_EVENT_FIELDS, 'Action Item public event');
  const kind = requiredString(event, 'kind', 'Action Item public event');
  if (!EVENT_KINDS.has(kind as ActionItemEventKind)) {
    throw new Error('Action Item public event kind is invalid');
  }
  const actorId = optionalString(event, 'actorId', 'Action Item public event');
  const assignmentId = optionalString(event, 'assignmentId', 'Action Item public event');
  const personnelId = optionalString(event, 'personnelId', 'Action Item public event');
  const fromStatus = optionalAssignmentStatus(event, 'fromStatus');
  const toStatus = optionalAssignmentStatus(event, 'toStatus');
  if (kind.startsWith('assignment_') && (!assignmentId || !personnelId)) {
    throw new Error('Action Item public assignment event identity is invalid');
  }
  if (kind === 'assignment_status_changed' && (!fromStatus || !toStatus)) {
    throw new Error('Action Item public status-change event is invalid');
  }
  return {
    id: requiredString(event, 'id', 'Action Item public event'),
    actionItemId: requiredString(event, 'actionItemId', 'Action Item public event'),
    kind: kind as ActionItemEventKind,
    occurredAt: requiredString(event, 'occurredAt', 'Action Item public event'),
    ...(actorId ? { actorId } : {}),
    ...(assignmentId ? { assignmentId } : {}),
    ...(personnelId ? { personnelId } : {}),
    ...(fromStatus ? { fromStatus } : {}),
    ...(toStatus ? { toStatus } : {}),
    summary: requiredString(event, 'summary', 'Action Item public event'),
  };
}

function isCanonicalEvidenceKey(value: string): boolean {
  if (!value.startsWith('v1:') || value.trim() !== value || value.includes('/') || value.includes('\\') ||
    !CANONICAL_EVIDENCE_KEY_CHARACTERS.test(value) || new TextEncoder().encode(value).byteLength > 1500) {
    return false;
  }
  const segments = value.split(':');
  if (segments.length < 4 || segments.some(segment => segment.length === 0)) return false;
  try {
    return segments.every(segment => encodeURIComponent(decodeURIComponent(segment)) === segment);
  } catch {
    return false;
  }
}

function decodeReviewedEvidenceKeys(value: unknown): string[] {
  if (!Array.isArray(value)) {
    throw new Error('Action Items public reviewedEvidenceKeys is invalid');
  }
  const candidates = value as unknown[];
  if (!candidates.every(key => typeof key === 'string' && isCanonicalEvidenceKey(key))) {
    throw new Error('Action Items public reviewedEvidenceKeys is invalid');
  }
  const keys = candidates.map(key => key as string);
  if (keys.some((key, index) => {
    const previous = keys[index - 1];
    return previous !== undefined && previous >= key;
  })) {
    throw new Error('Action Items public reviewedEvidenceKeys must be sorted and unique');
  }
  return keys;
}

export function decodeActionItemsManagementPayload(value: unknown): ActionItemsManagementPayload {
  const payload = strictRecord(
    value,
    new Set(['items', 'events', 'reviewedEvidenceKeys']),
    'Action Items public payload',
  );
  if (!Array.isArray(payload.items) || !Array.isArray(payload.events)) {
    throw new Error('Action Items public payload collections are invalid');
  }
  return {
    items: payload.items.map(decodePublicActionItem),
    events: payload.events.map(decodePublicActionItemEvent),
    reviewedEvidenceKeys: decodeReviewedEvidenceKeys(payload.reviewedEvidenceKeys),
  };
}

export interface SubscribeActionItemsManagementRequest {
  access: 'view' | 'edit';
}

export interface ActionItemsClient {
  subscribeManagement(
    scope: ProjectScope,
    request: SubscribeActionItemsManagementRequest,
    onUpdate: (snapshot: ActionItemsManagementSnapshot) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe;
}
